/*
 * Comparison Trail Dialog.
 *
 * Opened by clicking either the "PMC Present" or "Contractor Present"
 * KPI tile on the AttendanceComparePage. Shows side-by-side photos of
 * EVERY active employee in the park, captured by both PMC and
 * Contractor supervisors, across the past N days (default 3) ending at
 * the date the user has selected on the parent page.
 *
 * Layout — one row per employee, one column per date. Each date cell is
 * split into two halves: PMC photo(s) on top, Contractor photo(s) at
 * the bottom. Photo thumbnails show the capture timestamp and a small
 * face-match % chip. Empty cells render a clear "—" placeholder so the
 * admin can immediately see which supervisor missed a day.
 *
 * The role passed in `defaultRole` highlights the corresponding column
 * in each cell so the admin's mental focus matches the KPI they
 * clicked, but BOTH sides are always rendered side-by-side for
 * direct comparison (that's the whole point).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, HardHat, Camera, Loader2 } from "lucide-react";
import { fmtTime } from "@/lib/utils-app";
import { toast } from "sonner";

function PhotoTile({ cap }) {
  const [openImg, setOpenImg] = useState(false);
  if (!cap) {
    return (
      <div className="grid h-14 w-14 place-items-center rounded bg-slate-100 text-[10px] text-slate-400 ring-1 ring-slate-200">
        —
      </div>
    );
  }
  const src = cap.photo_b64
    ? (cap.photo_b64.startsWith("data:") ? cap.photo_b64 : `data:image/jpeg;base64,${cap.photo_b64}`)
    : null;
  const matchTone = cap.face_status === "match"
    ? "bg-emerald-500"
    : cap.face_status === "mismatch"
      ? "bg-rose-500"
      : cap.face_status === "no_face"
        ? "bg-amber-500"
        : "bg-slate-400";
  return (
    <>
      <button
        type="button"
        onClick={() => src && setOpenImg(true)}
        className="group relative h-14 w-14 flex-none overflow-hidden rounded ring-1 ring-slate-200 hover:ring-sky-400"
        title={`${cap.created_at ? fmtTime(cap.created_at) : ""}${cap.face_match_pct != null ? " · " + cap.face_match_pct + "%" : ""}`}
      >
        {src ? (
          <img src={src} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center bg-slate-100">
            <Camera className="h-4 w-4 text-slate-400" />
          </div>
        )}
        {cap.face_match_pct != null ? (
          <span className={`absolute bottom-0 left-0 rounded-tr px-1 text-[9px] font-semibold text-white ${matchTone}`}>
            {cap.face_match_pct}%
          </span>
        ) : null}
      </button>
      {openImg && src ? (
        <Dialog open onOpenChange={() => setOpenImg(false)}>
          <DialogContent className="max-w-2xl">
            <div className="flex items-center justify-center bg-slate-900/95">
              <img src={src} alt="" className="max-h-[75vh] object-contain" />
            </div>
            <div className="px-2 pb-1 pr-8 text-center text-xs text-muted-foreground">
              {cap.created_at ? fmtTime(cap.created_at) : ""}
              {cap.face_match_pct != null ? ` · face match ${cap.face_match_pct}%` : ""}
              {cap.inside_geofence === true ? " · inside geofence" : cap.inside_geofence === false ? " · outside geofence" : ""}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

function RoleCell({ caps, highlight }) {
  // Renders the photo(s) for one role × one date cell. If the role has
  // zero captures for that day, render a single empty tile so the
  // missing supervisor is visually obvious. If there are multiple, show
  // up to 3 thumbnails inline + a "+N" overflow chip.
  if (!caps || caps.length === 0) {
    return (
      <div className={`flex h-full items-center justify-center rounded-md p-1.5 ${highlight ? "bg-amber-50/50" : ""}`}>
        <PhotoTile cap={null} />
      </div>
    );
  }
  const visible = caps.slice(0, 3);
  const extra = caps.length - visible.length;
  return (
    <div className={`flex flex-wrap items-center gap-1 rounded-md p-1.5 ${highlight ? "bg-amber-50/50 ring-1 ring-amber-200" : ""}`}>
      {visible.map((c) => <PhotoTile key={c.id} cap={c} />)}
      {extra > 0 ? (
        <span className="grid h-14 w-14 place-items-center rounded bg-slate-100 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

export default function ComparisonTrailDialog({ park, date, days = 3, defaultRole = "pmc", onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!park?.id || !date) return;
    setLoading(true);
    api
      .get("/admin/attendance/compare-trail", { params: { park_id: park.id, date, days } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load comparison trail"))
      .finally(() => setLoading(false));
  }, [park?.id, date, days]);

  const dates = data?.dates || [];
  const rows = useMemo(() => data?.rows || [], [data]);

  // Aggregate totals for the header summary chips.
  const summary = useMemo(() => {
    const out = { pmc: 0, contractor: 0, missing_pmc: 0, missing_contractor: 0 };
    rows.forEach((r) => {
      let anyPmc = false; let anyCon = false;
      Object.values(r.by_date || {}).forEach((cell) => {
        if ((cell.pmc || []).length) { out.pmc += cell.pmc.length; anyPmc = true; }
        if ((cell.contractor || []).length) { out.contractor += cell.contractor.length; anyCon = true; }
      });
      if (!anyPmc) out.missing_pmc += 1;
      if (!anyCon) out.missing_contractor += 1;
    });
    return out;
  }, [rows]);

  const formatHeader = (d) => {
    try {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    } catch { return d; }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl p-0" data-testid="comparison-trail-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 pr-10">
          <DialogTitle className="flex flex-wrap items-baseline gap-2 text-base pr-2">
            <span className="font-semibold">Side-by-side capture trail · Past {days} days</span>
            <span className="text-xs font-normal text-muted-foreground">
              {park?.code ? park.code + " · " : ""}{park?.name} ·
              {dates.length ? ` ${dates[0]} → ${dates[dates.length - 1]}` : ""}
            </span>
            <span className="ml-auto flex flex-wrap items-center gap-2 text-[11px] font-normal">
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-sky-700 ring-1 ring-sky-200">
                <ShieldCheck className="h-3 w-3" /> PMC {summary.pmc}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 ring-1 ring-amber-200">
                <HardHat className="h-3 w-3" /> Contractor {summary.contractor}
              </span>
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-auto px-3 pb-3">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading captures…
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
              No employees in this park.
            </div>
          ) : (
            <table className="w-full border-collapse text-sm" data-testid="comparison-trail-table">
              <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur">
                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-2">Worker</th>
                  {dates.map((d) => (
                    <th key={d} className="px-2 py-2">{formatHeader(d)}</th>
                  ))}
                </tr>
                <tr className="border-b border-slate-200 text-[10px] font-medium text-slate-500">
                  <th className="px-2 py-1"></th>
                  {dates.map((d) => (
                    <th key={d} className="px-2 py-1">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="inline-flex items-center gap-1 text-sky-700">
                          <ShieldCheck className="h-3 w-3" /> PMC
                        </div>
                        <div className="inline-flex items-center gap-1 text-amber-700">
                          <HardHat className="h-3 w-3" /> Contractor
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employee_id} className="border-b border-slate-100 align-top hover:bg-slate-50/60">
                    <td className="px-2 py-2 align-middle">
                      <div className="font-medium text-slate-800">{r.employee_name || "—"}</div>
                      <div className="flex flex-wrap items-baseline gap-x-2 text-[10px] text-muted-foreground">
                        <span className="font-mono">{r.employee_code}</span>
                        {r.designation ? <span>· {r.designation}</span> : null}
                      </div>
                    </td>
                    {dates.map((d) => {
                      const cell = (r.by_date || {})[d] || { pmc: [], contractor: [] };
                      return (
                        <td key={d} className="px-2 py-1.5 align-middle">
                          <div className="grid grid-cols-2 gap-2">
                            <RoleCell caps={cell.pmc} highlight={defaultRole === "pmc"} />
                            <RoleCell caps={cell.contractor} highlight={defaultRole === "contractor"} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
